import React, { useEffect, useState } from 'react';
import { TrendingUp, Zap, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';

interface TopPrompt {
  id: string;
  name: string;
  description: string;
  execution_count: number;
  last_used: string;
  created_at: string;
  success_rate: number;
}

interface TopTool {
  id: string;
  name: string;
  description: string;
  prompts_using: number;
  server_id: string;
  total_calls: number;
  avg_latency_ms: number;
}

type ViewMode = 'prompts' | 'tools';

interface TopPerformersTableProps {
  limit?: number;
}

export const TopPerformersTable: React.FC<TopPerformersTableProps> = ({ limit = 10 }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('prompts');
  const [prompts, setPrompts] = useState<TopPrompt[]>([]);
  const [tools, setTools] = useState<TopTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [viewMode, limit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (viewMode === 'prompts') {
        const response = await api.analytics.getTopPrompts(limit);
        setPrompts(response.prompts);
      } else {
        const response = await api.analytics.getTopTools(limit);
        setTools(response.tools);
      }
    } catch (err) {
      console.error('Error fetching top performers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const renderPromptRow = (prompt: TopPrompt) => (
    <tr key={prompt.id} className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{prompt.name}</span>
          <span className="text-sm text-gray-500 truncate max-w-md">
            {prompt.description}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Zap className="h-4 w-4 text-yellow-500" />
          <span className="font-semibold text-gray-900">
            {prompt.execution_count.toLocaleString()}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-gray-700">{prompt.success_rate.toFixed(1)}%</span>
        </div>
      </td>
      <td className="px-6 py-4 text-center text-sm text-gray-600">
        {formatDate(prompt.last_used)}
      </td>
    </tr>
  );

  const renderToolRow = (tool: TopTool) => (
    <tr key={tool.id} className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{tool.name}</span>
          <span className="text-sm text-gray-500 truncate max-w-md">
            {tool.description}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {tool.prompts_using} prompts
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="font-semibold text-gray-900">
          {tool.total_calls.toLocaleString()}
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`text-sm font-medium ${
          tool.avg_latency_ms < 100 ? 'text-green-600' :
          tool.avg_latency_ms < 300 ? 'text-yellow-600' :
          'text-red-600'
        }`}>
          {tool.avg_latency_ms}ms
        </span>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="h-8 bg-gray-200 rounded w-48 mb-4 animate-pulse"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium mb-2">Failed to load data</p>
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header with toggle */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Top Performers
            </h3>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('prompts')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'prompts'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Prompts
            </button>
            <button
              onClick={() => setViewMode('tools')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'tools'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tools
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {viewMode === 'prompts' ? (
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prompt
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Executions
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Used
                </th>
              </tr>
            ) : (
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tool
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Used By
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Calls
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Latency
                </th>
              </tr>
            )}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {viewMode === 'prompts' ? (
              prompts.length > 0 ? (
                prompts.map(renderPromptRow)
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No prompts found
                  </td>
                </tr>
              )
            ) : (
              tools.length > 0 ? (
                tools.map(renderToolRow)
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No tools found
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
